import React, { ChangeEvent, MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash-es/debounce';
import styled from 'styled-components';
import { OcInput } from '../../../Input';
import { SystemIcon } from '../../../Icon/SystemIcon';
import { SystemIconButton } from '../../../Icon';
import { useIntlUds } from '../../../../translations/UdsIntlProvider';
import { useDataTableInitialSearchModel } from '../../utils/DataTableQueryProvider/DataTableInitialSearchModelProvider';

export interface SearchInputProps {
  value?: string;
  placeHolder?: string;
  onValueChange?: (name: string) => void;
  onKeyDown?(e: React.KeyboardEvent): void;
  debounceMs?: number;
  width?: string;
  dataTest?: string;
  fullWidth?: boolean;
  autoFocus?: boolean;
  helperText?: React.ReactNode;
  onInputRef?: (ref: MutableRefObject<HTMLInputElement | null>) => void;
  className?: string;
  onClickCapture?: React.MouseEventHandler<HTMLDivElement> | undefined;
}

export const SearchInput: React.FC<React.PropsWithChildren<SearchInputProps>> = ({
  placeHolder,
  value,
  helperText,
  debounceMs = 300,
  width,
  onValueChange,
  onKeyDown,
  dataTest,
  autoFocus = false,
  fullWidth = true,
  onInputRef,
  className,
  onClickCapture
}) => {
  const { intl } = useIntlUds();

  const placeholderIntl = placeHolder ?? intl.DataTable.SearchInput.placeholder;
  const { initialSearchModel } = useDataTableInitialSearchModel();
  const [localValue, setLocalValue] = useState<string>(value || '');
  const inputRef = useRef<HTMLInputElement>(null);
  const isInitializedSearchModel = !!initialSearchModel;

  useEffect(() => {
    onInputRef?.(inputRef);
  }, [onInputRef]);

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    if (isInitializedSearchModel && document.activeElement !== inputRef.current) {
      setLocalValue(value || '');
    }
  }, [isInitializedSearchModel, value]);

  const debouncedUpdate = useMemo(() => debounce(onValueChange ?? (() => {}), debounceMs), [onValueChange, debounceMs]);
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e?.currentTarget?.value ?? '';
      setLocalValue(newValue);
      debouncedUpdate(newValue);
    },
    [debouncedUpdate]
  );

  const handleClearSearch = useCallback(() => {
    inputRef.current?.focus();
    handleChange({ currentTarget: { value: '' } } as ChangeEvent<HTMLInputElement>);
  }, [handleChange]);

  return (
    <OcInput
      onClickCapture={onClickCapture}
      fullWidth={fullWidth}
      onChange={handleChange}
      className={className}
      placeholder={placeholderIntl}
      onKeyDown={onKeyDown}
      autoFocus={autoFocus}
      helperText={helperText}
      inputRef={inputRef}
      value={localValue}
      startAdornment={<StyledSearchIcon alt="search" name="search" />}
      endAdornment={
        localValue ? (
          <StyledCloseIcon size="small" dataTest="clear-search-btn" onClick={handleClearSearch} iconName="close" />
        ) : undefined
      }
      style={{ width }}
      dataTest={dataTest ?? 'search-input'}
    />
  );
};

const StyledCloseIcon = styled(SystemIconButton)`
    &.MuiIconButton-root {
        padding: ${({ theme }) => theme.spacingSize.m};
    }
`;

const StyledSearchIcon = styled(SystemIcon)`
    margin-left: ${({ theme }) => theme.spacingSize.s};
`;
